import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ResumeStepValidatable } from '@edtech-ui/core/models/resume-builder/step-validators';
import { ResumeService } from '@edtech-ui/core/services/resume-service/resume.service';
import { cgpaPercentageValidator, collegeNameValidator, degreeValidator } from '@edtech-ui/core/utils/resume-validation/resume-validation-education';
import { locationValidator } from '@edtech-ui/core/utils/resume-validation/resume-validation-personal';

@Component({
	selector: 'app-resumer-builder-education',
	standalone: true,
	imports: [CommonModule, ReactiveFormsModule],
	templateUrl: './resumer-builder-education.component.html',
	styleUrl: './resumer-builder-education.component.scss'
})
export class ResumerBuilderEducationComponent implements OnInit, ResumeStepValidatable {

	userProfileForm: FormGroup;

	months = [
		'January', 'February', 'March', 'April', 'May', 'June',
		'July', 'August', 'September', 'October', 'November', 'December'
	];

	years: number[] = [];

	constructor(
		private resumeService: ResumeService,
		private route: ActivatedRoute
	) {
		const currentYear = new Date().getFullYear();
		for (let year = 1980; year <= currentYear; year++) {
			this.years.push(year);
		}

		this.userProfileForm = new FormGroup({
			educations: new FormArray([this.createEducationGroup()])
		});
	}



	ngOnInit(): void {
		const resumeId = this.route.snapshot.paramMap.get('resumeId');
		if (resumeId) {
			this.resumeService.getStudentResumeById(resumeId).subscribe(res => {
				if (res.Success && res.Data?.education) {
					this.resumeService.setFullResumeData(res.Data);
					this.editEducationData(res.Data.education);
				}
			});
			return;
		}
		const data = this.resumeService.getSectionData('education');

		if (data && data.length) {
			this.educations.clear();

			data.forEach((edu: any) => {
				const group = this.createEducationGroup();

				group.patchValue({
					...edu,
					currently_studying: edu.currently_studying || false
				});

				this.educations.push(group);
			});
		}
	}


	createEducationGroup(): FormGroup {
		return new FormGroup({
			collegeName: new FormControl('', [Validators.required, collegeNameValidator]),
			degree: new FormControl('', [Validators.required, degreeValidator]),
			specialization: new FormControl(''),
			location: new FormControl('', [locationValidator]),
			startMonth: new FormControl(''),
			startYear: new FormControl(''),
			endMonth: new FormControl(''),
			endYear: new FormControl(''),
			currently_studying: new FormControl(false),
			cgpa_or_percentage: new FormControl('', [cgpaPercentageValidator])
		},
			{ validators: this.dateRangeValidator.bind(this) }
		);
	}


	get educations(): FormArray {
		return this.userProfileForm.get('educations') as FormArray;
	}


	addEducation(): void {
		this.educations.push(this.createEducationGroup());
	}


	removeEducation(index: number): void {
		if (this.educations.length > 1) {
			this.educations.removeAt(index);
		}
	}


	saveData() {
		if (this.userProfileForm.valid) {
			const payload = this.userProfileForm.value.educations;
			this.resumeService.setSectionData('education', payload);
		}
	}


	isCurrentlyStudying(index: number): boolean {
		return this.educations.at(index).get('currently_studying')?.value;
	}


	onCurrentlyStudyingChange(event: any, index: number) {
		const edu = this.educations.at(index);
		if (event.target.checked) {
			edu.patchValue({
				endMonth: 'Present',
				endYear: String(new Date().getFullYear())
			});
		} else {
			edu.patchValue({
				endMonth: '',
				endYear: ''
			});
		}
		edu.updateValueAndValidity();
	}


	editEducationData(data: any[]) {
		this.educations.clear();
		if (!data || !data.length) {
			this.educations.push(this.createEducationGroup());
			return;
		}
		data.forEach((edu: any) => {
			const group = this.createEducationGroup();

			group.patchValue({
				collegeName: edu.collegeName || '',
				degree: edu.degree || '',
				specialization: edu.specialization || '',
				location: edu.location || '',
				startMonth: edu.startMonth || '',
				startYear: edu.startYear || '',
				endMonth: edu.endMonth || '',
				endYear: edu.endYear || '',
				currently_studying: edu.currently_studying || false,
				cgpa_or_percentage: edu.cgpa_or_percentage || ''
			});
			this.educations.push(group);
		});
	}


	isFormValid(): boolean {
		return this.userProfileForm.valid;
	}


	getControl(index: number, field: string) {
		return this.educations.at(index).get(field);
	}

	dateRangeValidator(group: any) {
		const startMonth = group.get('startMonth')?.value;
		const startYear = group.get('startYear')?.value;
		const endMonth = group.get('endMonth')?.value;
		const endYear = group.get('endYear')?.value;
		const isCurrent = group.get('currently_studying')?.value;

		if (isCurrent) return null;

		if (!startMonth || !startYear || !endMonth || !endYear) {
			return null;
		}

		const monthIndex = (month: string) =>
			this.months.indexOf(month);

		const start = new Date(+startYear, monthIndex(startMonth));
		const end = new Date(+endYear, monthIndex(endMonth));

		if (end < start) {
			return { invalidDateRange: true };
		}

		return null;
	}


}